import {useMemo} from 'react'
import useSWR, {type Key, type SWRResponse} from 'swr'
import type {StrictTupleKey} from 'swr/_internal'

import type {Iri} from '../../@types/Api'
import type {CollectionMap, HydraCollection} from '../../@types/hydra/HydraCollection'
import type {HydraCollectionMember} from '../../@types/hydra/HydraCollectionMember'
import type {HydraItem} from '../../@types/hydra/HydraItem'
import {ApiError} from '../utils/ApiError'
import {createCollectionIri, iriToUuid} from '../utils/iri'
import {mapItems} from '../utils/map'
import {compareString} from '../utils/sort'

import {useAuthenticatedFetcher} from './useAuth'
import {useEvent} from './useEvent'
import {useMutateResource} from './useMutate'

export type Order = 'asc' | 'desc'

type AdvancedDateFilterOperator = 'before' | 'strictly_before' | 'after' | 'strictly_after' | 'exclude_null' | 'include_null_before' | 'include_null_after' | 'include_null_before_and_after'
type AdvancedNumericFilterOperator = 'lt' | 'gt' | 'lte' | 'gte' | 'between'

export interface AdvancedFilter {
  operator: AdvancedNumericFilterOperator | AdvancedDateFilterOperator
  value: string
}
export interface FetchSortOption<S extends string = never> {
  field: S
  order: Order
}

export interface FetchOption<S extends string = never> {
  sort?: FetchSortOption<S>
  page?: number
  itemsPerPage?: number
  pagination?: boolean
}

type CollectionHook<T extends HydraCollectionMember> = {
  readonly items?: HydraCollection<T>
  readonly error?: Error
}

type Filter = {
  readonly iri?: Iri
  readonly iris?: Iri[]
}

export function applyFetchOptions<S extends string = never>(querystring: URLSearchParams, fetchOptions: FetchOption<S> | undefined): void {
  if (fetchOptions?.page !== undefined) {
    querystring.set('page', `${fetchOptions.page + 1}`)
    querystring.set('pagination', 'true')
  }

  if (fetchOptions?.itemsPerPage !== undefined) {
    querystring.set('itemsPerPage', `${fetchOptions.itemsPerPage}`)
    querystring.set('pagination', 'true')
  }

  if (fetchOptions?.sort) {
    querystring.set(`order[${fetchOptions.sort.field}]`, fetchOptions.sort.order)
  } else if (!querystring.has('pagination')) {
    querystring.set('pagination', 'false')
  }
}

export function useItems<T extends HydraCollectionMember, S extends string = never>(resource: string, filter?: Filter, fetchOptions?: FetchOption<S>): CollectionHook<T> {
  const querystring = new URLSearchParams()
  let empty = true
  if (filter?.iri) {
    querystring.set('id', iriToUuid(filter.iri))
    empty = false
  }
  if (filter?.iris) {
    for (const [i, iri] of filter.iris.entries()) {
      querystring.set(`id[${i}]`, iriToUuid(iri))
      empty = false
    }
  }
  applyFetchOptions(querystring, fetchOptions)
  const {data, error} = useSWR(!empty ? `${createCollectionIri(resource)}?${querystring.toString()}` : null)

  return {
    items: data,
    error,
  }
}

type FilterValue = boolean | number | string | AdvancedFilter

type AllKeysOf<T> = T extends unknown ? keyof T : never
export type FilterDefinitionMapping<T> = Record<AllKeysOf<T>, string>

// TypeScript's excess-property check only fires on fresh literals; the `never` keys reject a mixed filter
// object passed through a variable too.
export type StrictUnion<T> = StrictUnionMember<T, T>
type StrictUnionMember<T, TAll> = T extends unknown ? T & Partial<Record<Exclude<AllKeysOf<TAll>, keyof T>, never>> : never

export function filterToQueryString<S extends string = never>(
  mapping: Readonly<Record<string, string>>,
  filter: Readonly<Record<string, undefined | FilterValue | readonly FilterValue[]>> | undefined,
  fetchOptions: FetchOption<S> | undefined,
): URLSearchParams | undefined {
  if (!filter) {
    return undefined
  }

  const querystring = new URLSearchParams()

  const filterKeys = new Set(Object.keys(filter))
  for (const [filterKey, queryParam] of Object.entries<string>(mapping)) {
    if (!filterKeys.has(filterKey)) {
      continue
    }

    const filterValue = filter[filterKey]
    if (filterValue === undefined) {
      continue
    }

    if (Array.isArray(filterValue) && typeof filterValue[0] !== 'object') {
      if (filterValue.length === 0) {
        querystring.set('__empty_list__', '1')
        continue
      }

      const filterValueUnique = [...new Set(filterValue as string[])].toSorted(compareString)
      for (const [i, value] of filterValueUnique.entries()) {
        querystring.set(`${queryParam}[${i}]`, value)
      }
    } else if (typeof filterValue === 'boolean') {
      querystring.set(queryParam, filterValue ? 'true' : 'false')
    } else if (typeof filterValue === 'number') {
      querystring.set(queryParam, filterValue.toString())
    } else if (typeof filterValue === 'object') {
      for (const advanced of (Array.isArray(filterValue) ? filterValue : [filterValue]) as AdvancedFilter[]) {
        querystring.set(`${queryParam}[${advanced.operator}]`, advanced.value)
      }
    } else {
      querystring.set(queryParam, filterValue as string)
    }
  }

  applyFetchOptions(querystring, fetchOptions)

  return querystring
}

export function useGet<Data = any, Error = any, SWRKey extends Key = StrictTupleKey>(key: SWRKey): SWRResponse<Data, Error | ApiError> {
  const is404 = typeof key === 'string' && key.endsWith('/00000000-0000-0000-0000-000000000000')
  const response = useSWR<Data, Error, SWRKey | undefined>(is404 ? undefined : key)
  const {data, error, mutate} = response
  if (data && error && error instanceof ApiError && error.statusCode === 404) {
    setTimeout(() => mutate(undefined), 10)
  }

  if (is404) {
    return {
      ...response,
      data: undefined,
      error: new ApiError('item not found', 404, 'item not found'),
    }
  }

  return response
}

export function useMap<T extends HydraCollectionMember>(collection: HydraCollection<T> | undefined): CollectionMap<T> {
  return useMemo<CollectionMap<T>>(() => {
    return mapItems<T>(collection?.member)
  }, [collection])
}

export function usePatch<T extends HydraItem>(): (item: HydraCollectionMember, payload: object) => Promise<T> {
  const authenticatedFetcher = useAuthenticatedFetcher()
  const mutateResource = useMutateResource()

  return useEvent(async (item: HydraCollectionMember, payload: object) => {
    const response = await authenticatedFetcher(item['@id'], {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
    await mutateResource(item['@id'], response)

    return response
  })
}

export function usePost<T extends HydraItem>(resource: string): (payload: object) => Promise<T> {
  const authenticatedFetcher = useAuthenticatedFetcher()
  const mutateResource = useMutateResource()

  return useEvent(async (payload: object) => {
    const response = await authenticatedFetcher(createCollectionIri(resource), {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    await mutateResource(response['@id'], response)

    return response
  })
}

export function useDelete(): (item: HydraCollectionMember) => Promise<void> {
  const authenticatedFetcher = useAuthenticatedFetcher()
  const mutateResource = useMutateResource()

  return useEvent(async (item: HydraCollectionMember) => {
    await authenticatedFetcher(item['@id'], {
      method: 'DELETE',
    })
    await mutateResource(item['@id'])
  })
}

export function useUpsert<T extends HydraItem, TU extends HydraCollectionMember, UC extends object, UU extends object>(
  itemCreate: (payload: UC) => Promise<T>,
  itemUpdate: (item: TU, payload: UU) => Promise<T>,
): (item: TU | undefined, payload: UU, createExtraPayload?: Omit<UC, keyof UU>) => Promise<T> {
  return useEvent(async (item: TU | undefined, payload: UU, createExtraPayload?: Omit<UC, keyof UU>): Promise<T> => {
    if (item) {
      return itemUpdate(item, payload)
    }

    // @ts-expect-error
    const createPayload: UC = {...createExtraPayload, ...payload}
    return itemCreate(createPayload)
  })
}
